<template>
  <b-modal
    id="modal-contact-form"
    class="modal-contact-form"
    size="md"
    centered
    hide-header-close
    no-close-on-esc
    no-close-on-backdrop
    :title="title"
    @hidden="handleHide"
  >
    <FormulateForm
      ref="formRef"
      v-model="formValues"
      class="card-body modal-contact-form__body"
      :errors="formErrors"
    >
      <b-row>
        <b-col v-for="field in fields" :key="field.key" md="6" cols="12">
          <FormulateInput v-bind="field" class="p-0" :disabled="isLoading">
            <template v-if="field.prefix" #prefix>
              <div class="formulate-input-prefix">
                <span>{{ field.prefix }}</span>
              </div>
            </template>
          </FormulateInput>
        </b-col>
      </b-row>

      <template v-if="!isNotificationLocationFlagEnabled">
        <div class="modal-contact-form__checkbox">
          <McCheckbox
            id="checkbox-map"
            v-model="isShowMap"
            :disabled="isLoading"
            @change="handleClickNotifyMe"
          />
          <label for="checkbox-map">Notifikasi kendaraan tiba</label>
          <i
            :id="`tooltip-notif-vehicle`"
            class="mc-fa"
            :class="{
              'icon-outline_info': !hoveredKey,
              'icon-fill_info': hoveredKey,
            }"
            @mouseenter="hoveredKey = true"
            @mouseleave="hoveredKey = false"
          />
          <b-popover
            triggers="hover"
            placement="bottomleft"
            :target="`tooltip-notif-vehicle`"
          >
            <div class="popover-text-wrapper">
              {{ 'kirimkan notifikasi ketika kendaraan mendekati dan sampai di alamat yang  ditentukan' }}
            </div>
          </b-popover>
          <McBadge
            :text="$t('global.new')"
            variant="danger"
            size="small"
            type="pill"
            class="text-uppercase float-end custom-badge"
          />
        </div>
  
        <MapSelectPoint
          v-if="isShowMap"
          :modelValue="formValues.location"
          @update:modelValue="$set(formValues, 'location', $event)"
        />
      </template>
    </FormulateForm>

    <template #modal-footer="{ cancel }">
      <div class="modal-contact-form__footer">
        <div class="__copy-btn">
          <McButton
            v-if="isShowCopyLink"
            variant="secondary"
            text="Salin Tautan"
            icon="icon-outline_link-2"
            :disabled="isLoading"
            @click="() => handleCopyLink(cancel)"
          />
        </div>

        <div class="__action-btns">
          <McButton
            variant="secondary"
            :text="cancelTitle"
            :disabled="isLoading"
            @click="() => handleCancel(cancel)"
          />
          <McButton :text="confirmTitle" :disabled="isLoading" @click="handleSubmit" />
        </div>
      </div>
    </template>
  </b-modal>
</template>

<script>
import { MixPanel } from '@/mixin/mixpanel'

import McButton from '@/components/McButton'
import McCheckbox from '@/components/McCheckbox'
import McBadge from '@/components/McBadge'
import MapSelectPoint from '../MapSelectPoint'

import { createContactFields } from '../../lib/fields/create-contact-fields'
import { createContact as defaultFormValues } from '../../lib/constants/default-values'
import { GOOGLE_MAP_ADDRESS_TYPE } from '../../lib/constants/types'

import { mapGetters } from "vuex"

export default {
  components: {
    McButton,
    McCheckbox,
    McBadge,
    MapSelectPoint,
  },
  mixins: [MixPanel],
  props: {
    title: {
      type: String,
      default: 'Tambah Kontak Penerima',
    },
    cancelTitle: {
      type: String,
      default: 'Batal',
    },
    confirmTitle: {
      type: String,
      default: 'Tambahkan',
    },
    isShowCopyLink: {
      type: Boolean,
      default: false,
    },
    link: {
      type: String,
      default: '',
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['cancel', 'create:contact'],
  data() {
    return {
      isShowMap: false,
      hoveredKey: false,

      formValues: { ...defaultFormValues },
      formErrors: {},
    }
  },
  computed: {
    ...mapGetters({
      getFeatureFlagData: 'identity/getFeatureFlagData',
    }),

    fields() {
      return createContactFields()
    },

    isNotificationLocationFlagEnabled() {
      return this.getFeatureFlagData('notification.temporary_track').value
    }
  },
  watch: {
    modalType() {
      this.formValues = { ...defaultFormValues }
    },

    formValues(newValue, oldValue) {
      // skip logic if there is no error and the phoneNumber value is not changing
      if (Object.keys(this.formErrors).length === 0) return
      if (newValue.phoneNumber === oldValue.phoneNumber) return

      this.$delete(this.formErrors, 'phoneNumber')
    },
  },
  methods: {
    SetFormErrors(response) {
      if (response.errorName === 'PhoneNumberAlreadyExists') {
        this.$set(this.formErrors, 'phoneNumber', 'No. Whatsapp sudah terdaftar di sistem.')
      } else if (response.errorName === 'PhoneNumberInvalid') {
        this.$set(this.formErrors, 'phoneNumber', 'No. Whatsapp tidak valid.')
      }
    },

    handleCopyLink() {
      this.track('Pelacakan Sementara Copy Link Clicked')

      navigator.clipboard.writeText(this.link)
      this.$mcToast.success(`Tautan berhasil disalin`)
    },

    resetState() {
      this.formValues = { ...defaultFormValues }
      this.formErrors = {}
      this.isShowMap = false
    },

    handleCancel(cancelFunc) {
      this.resetState()

      cancelFunc()
      this.$emit('cancel')
    },

    handleHide() {
      this.resetState()
    },

    handleClickNotifyMe(value) {
      this.track('Pelacakan Sementara Notify Me Checked', {
        Notify_Checked: value,
      })
    },

    handleSubmit() {
      if (this.isShowCopyLink) {
        this.track('Pelacakan Sementara Send Clicked')
      }

      if (this.$refs.formRef.hasErrors) {
        this.$refs.formRef.showErrors()
        this.$mcToast.error('Terdapat kesalahan format pada input anda')
        return
      }

      if (this.isShowMap && this.formValues.location === null) {
        this.$mcToast.error('Lokasi belum dipilih!')
        return
      }

      const newContact = {
        ...this.formValues,
      }

      if (
        this.isShowMap &&
        newContact.location?.type === GOOGLE_MAP_ADDRESS_TYPE &&
        !isNaN(parseFloat(newContact?.location.id))
      ) {
        delete newContact.location.id
      }

      this.$emit('create:contact', newContact)
    },
  },
}
</script>

<style lang="scss" scoped>
.modal-contact-form {
  &__body {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  &__checkbox {
    display: flex;
    gap: 8px;
    color: $mep-color-text-400;
    align-items: center;

    label {
      margin-bottom: 0;
    }

    .custom-badge {
      font-size: 10px;
    }
  }

  &__footer {
    width: 100%;
    display: flex;
    justify-content: space-between;
    gap: 12px;

    .__action-btns {
      display: flex;
      gap: 12px;
    }
  }
}

.popover-text-wrapper {
  font-family: $font-family-sans-serif;
  font-size: 12px;
  background-color: $mep-color-text-400 !important;
  color: $white !important;
  max-width: 320px;
  min-width: fit-content;
  padding: 6px 12px;
}
</style>
